/**
 * Retail Reports (Sprint 1, Oct 2026): the seven standard reports.
 *
 * Read-only views over `GET /api/retail/reports/standard/:id`. A report
 * opens with results; changing its settings shows "Settings changed" and
 * an Apply button (no query per click). Excel / CSV export is for business
 * admins; a demo viewer sees the controls locked.
 *
 * Deliberately absent (Sprint 2): the Custom Report Builder and saved
 * reports. Nothing here sends a report definition to the server — only an
 * allowlisted report id and allowlisted filter values.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Download, FileSpreadsheet, FileText, Info, Lock, Play } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import DashboardLayout from "../components/DashboardLayout";
import { PERIODS, count, money, rangeLabel, reportError } from "../lib/retailReporting";

const FILTERS_BY_DATASET = {
  items: ["catalog_id", "fulfillment", "payment", "member", "family", "category"],
  orders: ["catalog_id", "fulfillment", "payment", "member"],
};

export function ReportsLandingContent() {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/retail/reports/standard").then((r) => setReports(r.data.reports)).catch((e) => setError(reportError(e)));
  }, []);
  return (
    <div className="flex max-w-[1480px] flex-col gap-[18px]" data-testid="reports-landing">
      <header>
        <h1 className="font-display text-3xl font-semibold">Reports</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">Ready-made sales reports. Change the period or filters, and export to Excel or CSV.</p>
      </header>
      {error && <div role="alert" className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900">{error}</div>}
      <div>
        <h2 className="font-display text-[19px] font-semibold">Standard reports</h2>
        <p className="text-[13px] text-muted-foreground">Open a report to change its period or filters.</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {(reports || []).map((r) => (
          <Link key={r.id} to={`/dashboard/reports/${r.id}`} data-testid={`report-card-${r.id}`}
            className="flex flex-col gap-2 rounded-xl border bg-card p-4 shadow-sm transition hover:border-foreground/30">
            <h3 className="font-display text-base font-semibold">{r.name}</h3>
            <p className="text-[13px] text-muted-foreground">{r.description}</p>
            <div className="mt-auto border-t pt-1.5 text-[12.5px] text-foreground/80">
              {r.rows} · by {r.groups.map((g) => g.label).join(" → ")}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function fmt(col, v) {
  if (v === null || v === undefined) return "";
  return col.format === "money" ? money(v) : count(v);
}

function GroupLabel({ row }) {
  return (
    <>
      {row.label}
      {row.sub && <span className="ml-1.5 font-normal text-muted-foreground">{row.sub}</span>}
    </>
  );
}

function ReportTable({ data, onSort }) {
  const two = data.report.groups.length > 1;
  const [open, setOpen] = useState(() => new Set(data.rows.slice(0, 3).map((r) => String(r.key))));
  const cols = data.report.columns;
  const sortIcon = (k) => (data.sort.key === k ? (data.sort.dir === "asc" ? " ↑" : " ↓") : "");
  if (!data.rows.length) {
    return (
      <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="report-empty">
        No sales match these settings. Try a longer period or remove a filter.
      </div>
    );
  }
  const s = data.summary;
  return (
    <>
      <div className="overflow-x-auto border-t">
        <table className="w-full border-collapse text-[13.5px] tabular-nums" data-testid="report-table">
          <thead>
            <tr>
              <th scope="col" className="whitespace-nowrap border-b bg-secondary/70 px-3 py-2.5 text-left text-xs font-semibold text-foreground/80">
                <button type="button" onClick={() => onSort("group")}>{data.report.groups.map((g) => g.label).join(" → ")}{sortIcon("group")}</button>
              </th>
              {cols.map((c) => (
                <th key={c.key} scope="col" className="whitespace-nowrap border-b bg-secondary/70 px-3 py-2.5 text-right text-xs font-semibold text-foreground/80">
                  <button type="button" onClick={() => onSort(c.key)}>{c.label}{sortIcon(c.key)}</button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const k = String(r.key);
              const isOpen = !two || open.has(k);
              return (
                <React.Fragment key={k}>
                  <tr className={`${two ? "font-semibold" : ""} hover:bg-secondary/50`}>
                    <td className="whitespace-nowrap border-b px-3 py-2">
                      {two ? (
                        <button type="button" aria-expanded={isOpen} className="inline-flex items-center gap-1.5 font-semibold"
                          onClick={() => setOpen((s0) => { const n = new Set(s0); if (n.has(k)) n.delete(k); else n.add(k); return n; })}>
                          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}<GroupLabel row={r} />
                        </button>
                      ) : <GroupLabel row={r} />}
                    </td>
                    {cols.map((c) => <td key={c.key} className="whitespace-nowrap border-b px-3 py-2 text-right">{fmt(c, r.values[c.key])}</td>)}
                  </tr>
                  {two && isOpen && (r.children || []).map((ch) => (
                    <tr key={`${k}/${ch.key}`} className="text-foreground/80 hover:bg-secondary/50">
                      <td className="whitespace-nowrap border-b py-2 pl-10 pr-3"><GroupLabel row={ch} /></td>
                      {cols.map((c) => <td key={c.key} className="whitespace-nowrap border-b px-3 py-2 text-right">{fmt(c, ch.values[c.key])}</td>)}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr className="bg-secondary/70 font-bold" data-testid="report-total">
              <td className="border-t px-3 py-2">Total</td>
              {cols.map((c) => <td key={c.key} className="whitespace-nowrap border-t px-3 py-2 text-right">{fmt(c, data.totals[c.key])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2.5 border-t px-3.5 py-2.5 text-[12.5px] text-muted-foreground" data-testid="report-footer">
        <span>
          {count(s.groups)} {s.group_plural}
          {s.units !== null && s.units !== undefined ? ` · ${count(s.units)} units sold` : ""}
          {` · ${count(s.orders)} orders`}
          {two ? " · click a row to expand" : ""}
        </span>
        {data.overlapping_order_counts && (
          <span>An order with items in several groups is counted once in each, so group order counts can add up to more than the total.</span>
        )}
        {data.page.total_groups > data.rows.length && (
          <span>Showing the first {count(data.rows.length)} of {count(data.page.total_groups)} rows. Export to see all of them.</span>
        )}
      </div>
    </>
  );
}

function SettingSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="rounded-[9px] border bg-card px-2.5 py-1.5 text-[13.5px] text-foreground">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

const DEFAULT_SETTINGS = {
  period: "last30", start: "", end: "", catalog_id: "", fulfillment: "", payment: "",
  member: "", family: "", category: "", split_by_catalog: false,
};

export function ReportViewContent({ reportId }) {
  const { user } = useAuth() || {};
  const [draft, setDraft] = useState(DEFAULT_SETTINGS);
  const [applied, setApplied] = useState(DEFAULT_SETTINGS);
  const [sort, setSort] = useState({ key: null, dir: null });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(false);
  const [exporting, setExporting] = useState("");

  const params = useMemo(() => {
    const p = { period: applied.period };
    if (applied.period === "custom") { p.start = applied.start; p.end = applied.end; }
    ["catalog_id", "fulfillment", "payment", "member", "family", "category"].forEach((k) => { if (applied[k]) p[k] = applied[k]; });
    if (applied.split_by_catalog) p.split_by_catalog = true;
    if (sort.key) { p.sort = sort.key; p.dir = sort.dir; }
    return p;
  }, [applied, sort]);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return api.get(`/retail/reports/standard/${encodeURIComponent(reportId)}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(reportError(e)))
      .finally(() => setLoading(false));
  }, [reportId, params]);
  useEffect(() => { load(); }, [load]);

  const stale = JSON.stringify(draft) !== JSON.stringify(applied);
  const canExport = Boolean(data?.can_export) && user?.role !== "demo_viewer";
  const dataset = data?.report?.dataset || "items";
  const opts = data?.filter_options;
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const onSort = (key) => setSort((s0) => ({ key, dir: s0.key === key && s0.dir === "desc" ? "asc" : "desc" }));

  const doExport = async (format) => {
    setMenu(false);
    setExporting(format);
    try {
      const r = await api.get(`/retail/reports/standard/${encodeURIComponent(reportId)}/export`, {
        params: { ...params, format }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportId}_${data.period.start_date}_${data.period.end_date}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "The export didn't finish. Please try again.";
      try {
        const body = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()) : e?.response?.data;
        if (body?.detail?.message) msg = body.detail.message;
      } catch (_) { /* keep the generic message */ }
      setError(msg);
    } finally {
      setExporting("");
    }
  };

  const filterVisible = (k) => FILTERS_BY_DATASET[dataset].includes(k);
  const catalogs = opts?.catalogs || [];

  return (
    <div className="flex max-w-[1480px] flex-col gap-[18px]" data-testid="report-view">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
            <Link to="/dashboard/reports" className="underline underline-offset-2">Reports</Link><span aria-hidden="true">›</span><span>Standard report</span>
          </div>
          <h1 className="font-display text-3xl font-semibold">{data?.report?.name || "Report"}</h1>
          {data?.report?.description && <p className="mt-1 text-[13px] text-muted-foreground">{data.report.description}</p>}
        </div>
        <div className="relative">
          {canExport ? (
            <>
              <button type="button" onClick={() => setMenu((m) => !m)} aria-haspopup="menu" aria-expanded={menu} disabled={!data || !!exporting}
                data-testid="export-button"
                className="inline-flex items-center gap-2 rounded-[9px] border bg-card px-3.5 py-2 text-[13.5px] font-medium shadow-sm hover:bg-secondary disabled:opacity-60">
                <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export"} <ChevronDown className="h-4 w-4" />
              </button>
              {menu && (
                <div role="menu" className="absolute right-0 top-[calc(100%+6px)] z-30 w-64 rounded-[10px] border bg-card p-1.5 shadow-lg">
                  <div className="border-b px-2.5 pb-2 pt-1.5 text-xs text-muted-foreground">Exports the period, filters, grouping and sort shown here.</div>
                  <button type="button" role="menuitem" onClick={() => doExport("xlsx")} data-testid="export-xlsx"
                    className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13.5px] hover:bg-secondary">
                    <FileSpreadsheet className="h-4 w-4" /> Excel (.xlsx)
                  </button>
                  <button type="button" role="menuitem" onClick={() => doExport("csv")} data-testid="export-csv"
                    className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13.5px] hover:bg-secondary">
                    <FileText className="h-4 w-4" /> CSV
                  </button>
                </div>
              )}
            </>
          ) : (
            <button type="button" disabled title="View-only access. Ask the account owner to export reports." data-testid="export-locked"
              className="inline-flex items-center gap-2 rounded-[9px] border bg-card px-3.5 py-2 text-[13.5px] font-medium opacity-60">
              <Lock className="h-4 w-4" /> Export
            </button>
          )}
        </div>
      </header>

      {!canExport && data && (
        <div className="flex items-start gap-2 rounded-lg bg-secondary/70 px-3 py-2 text-[12.5px]" data-testid="view-only-note">
          <Lock className="mt-0.5 h-3.5 w-3.5 flex-none" /><span>You have view-only access. You can change what this report shows, but you can't export it.</span>
        </div>
      )}

      <section className="rounded-xl border bg-card shadow-sm" aria-label="Report settings">
        <div className="flex flex-wrap items-end gap-3 px-4 py-3">
          <SettingSelect label="Period" value={draft.period} onChange={set("period")} testid="report-period"
            options={PERIODS.map((x) => ({ value: x.key, label: x.label }))} />
          {draft.period === "custom" && (
            <>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">From
                <input type="date" value={draft.start} onChange={(e) => set("start")(e.target.value)} className="rounded-[9px] border bg-card px-2.5 py-1.5 text-[13.5px] text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">To
                <input type="date" value={draft.end} onChange={(e) => set("end")(e.target.value)} className="rounded-[9px] border bg-card px-2.5 py-1.5 text-[13.5px] text-foreground" />
              </label>
            </>
          )}
          {catalogs.length > 1 && (
            <SettingSelect label="Catalog" value={draft.catalog_id} onChange={set("catalog_id")} testid="report-catalog"
              options={[{ value: "", label: "All catalogs" }, ...catalogs.map((c) => ({ value: c.catalog_id, label: c.display_code ? `${c.name} · ${c.display_code}` : (c.name || c.catalog_id) }))]} />
          )}
          {filterVisible("fulfillment") && (
            <SettingSelect label="Fulfillment method" value={draft.fulfillment} onChange={set("fulfillment")} testid="report-fulfillment"
              options={[{ value: "", label: "All" }, ...(opts?.fulfillment || []).map((o) => ({ value: o.key, label: o.label }))]} />
          )}
          {filterVisible("payment") && (
            <SettingSelect label="Payment method" value={draft.payment} onChange={set("payment")} testid="report-payment"
              options={[{ value: "", label: "All" }, ...(opts?.payment || []).map((o) => ({ value: o.key, label: o.label }))]} />
          )}
          {filterVisible("member") && (
            <SettingSelect label="Member" value={draft.member} onChange={set("member")} testid="report-member"
              options={[{ value: "", label: "All orders" }, { value: "member", label: "Member orders" }, { value: "non_member", label: "Non-member orders" }]} />
          )}
          {filterVisible("family") && (opts?.families || []).length > 1 && (
            <SettingSelect label="Product family" value={draft.family} onChange={set("family")} testid="report-family"
              options={[{ value: "", label: "All" }, ...opts.families.map((o) => ({ value: o.key, label: o.label }))]} />
          )}
          {filterVisible("category") && (opts?.categories || []).length > 0 && (
            <SettingSelect label="Category" value={draft.category} onChange={set("category")} testid="report-category"
              options={[{ value: "", label: "All" }, ...opts.categories.map((c) => ({ value: c, label: c }))]} />
          )}
          {catalogs.length > 1 && (
            <label className="flex items-center gap-2 pb-2 text-[13px]">
              <input type="checkbox" checked={draft.split_by_catalog} onChange={(e) => set("split_by_catalog")(e.target.checked)} data-testid="report-split-catalog" />
              Split by catalog
            </label>
          )}
          <button type="button" onClick={() => setApplied(draft)} disabled={!stale || (draft.period === "custom" && !(draft.start && draft.end))}
            data-testid="report-apply"
            className="ml-auto inline-flex items-center gap-1.5 rounded-[9px] bg-primary px-3.5 py-2 text-[13.5px] font-medium text-primary-foreground disabled:opacity-50">
            <Play className="h-3.5 w-3.5" /> Apply
          </button>
        </div>
        {stale && (
          <div className="mx-4 mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900" data-testid="report-stale">
            <Info className="h-3.5 w-3.5" /> Settings changed. Apply to refresh the results.
          </div>
        )}
      </section>

      {error && <div role="alert" className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900" data-testid="report-error"><Info className="mt-0.5 h-4 w-4 flex-none" />{error}</div>}

      {data && (
        <section className="rounded-xl border bg-card shadow-sm min-w-0" aria-label="Report results" style={{ opacity: loading ? 0.6 : 1 }}>
          <div className="flex flex-wrap items-center gap-1.5 px-4 py-3 text-[12.5px]">
            <span className="rounded-full border px-2.5 py-0.5"><b>{data.period.label}</b> {rangeLabel(data.period.start_date, data.period.end_date)}</span>
            <span className="rounded-full border px-2.5 py-0.5">{data.report.rows}</span>
            <span className="rounded-full border px-2.5 py-0.5">Grouped by <b>{data.report.groups.map((g) => g.label).join(" → ")}</b></span>
            <span className="rounded-full border px-2.5 py-0.5 text-muted-foreground">Times in {data.period.timezone.replace(/_/g, " ")}</span>
          </div>
          <ReportTable
            key={[data.report.id, data.split_by_catalog, JSON.stringify(data.filters), data.period.start_date,
              data.period.end_date, data.sort.key, data.sort.dir].join("|")}
            data={data} onSort={onSort} />
        </section>
      )}
      {!data && loading && <div className="h-40 animate-pulse rounded-xl bg-secondary/60" />}
    </div>
  );
}

export function RetailReportsPage() {
  return <DashboardLayout><ReportsLandingContent /></DashboardLayout>;
}

export function RetailReportViewPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  useEffect(() => { if (!reportId) navigate("/dashboard/reports"); }, [reportId, navigate]);
  return <DashboardLayout><ReportViewContent reportId={reportId} /></DashboardLayout>;
}
